/**
 * POZO-ANIMATION-THEMES.JS — Catálogo de temas de animación para disparo de pozo
 *
 * SINGLE SOURCE OF TRUTH para:
 *   - Timing de cada fase de la animación (aceleración, warp, frenado, celebración)
 *   - Textos i18n de la celebración (ganador, PV, próximo pozo, CTA)
 *   - Presets de rendimiento por tier de hardware (partículas, estrellas)
 *   - Duración mínima y máxima aceptable
 *
 * CONSUMIDO POR:
 *   - shared/components/pozo-animation/ (clase PozoAnimation lee timing de aquí)
 *   - front-tv/js/systems/pozo-animation-manager.js (resuelve tema + i18n)
 *   - front-admin (selector de tema en modal de pozos)
 *   - dev/animations/{tema}/index.html (playground de desarrollo)
 *
 * CONVENCIÓN DE NOMBRES:
 *   - Cada nombre de variable narra la historia de NEGOCIO, no de implementación.
 *   - Los tiempos se expresan en milisegundos (sufijo Ms).
 *   - Los idiomas siguen el estándar del sistema: es, en, pt, ru.
 *
 * IDIOMAS SOPORTADOS: es (español), en (inglés), pt (portugués), ru (ruso)
 */

// ════════════════════════════════════════════════════════════════════════════
// CATÁLOGO DE TEMAS
// ════════════════════════════════════════════════════════════════════════════

const POZO_ANIMATION_THEMES = {
    'vegas-gold': {
        themeIdentifier: 'vegas-gold',
        themeDisplayName: 'Vegas Gold',
        themeDescription: 'Lluvia de monedas doradas con rodillo slot machine estilo Las Vegas',

        // ── Fondo: video loop decodificado por VPU hardware ──
        // Path relativo al root del front-tv (Cloudflare Pages sirve /assets/ desde public/)
        // Convención: pozo-bg-{themeIdentifier}.mp4 — un archivo por tema
        backgroundVideoPath: '/assets/pozo-bg-vegas-gold.mp4',

        // ── Entrada: "flashbang" adaptativo ──
        // 3 flashes en loop mientras el JS pre-renderiza título, reel, cifra inicial.
        // Cuando el render termina, la clase .intro-done hace que termine el ciclo actual y pare.
        // Un overlay oscuro tapa los gaps entre flashes para que el render no se note.
        // El sonido se dispara UNA vez al arrancar y tiene la duración de un ciclo completo.
        introFlashbangConfig: {
            // Duración de UN ciclo completo de 3 flashes. Se repite mientras haga falta.
            singleCycleDurationMs: 850,
            // Opacity del overlay oscuro que tapa los gaps entre flashes (0 = transparente, 1 = negro puro).
            // Sube a ~0.92 para cortina SÓLIDA que tape completamente el pre-render detrás
            // (si baja, se vería el setup de los elementos internos filtrándose).
            darkOverlayOpacity: 0.92,
            // Duración del fade-out del overlay oscuro cuando el render termina
            darkOverlayFadeOutMs: 400,
            // Colores y opacidades de los 3 flashes (WCAG: intensidad decreciente anti-epilepsia)
            flash1Color: 'rgba(255, 255, 255, 1.0)', // golpe 1: blanco puro explosivo
            flash2Color: 'rgba(255, 215, 80, 0.78)', // golpe 2: dorado cálido
            flash3Color: 'rgba(255, 170, 0, 0.55)', // golpe 3: dorado tenue (cierre)
            // Identificador del sonido en POZO_SOUND_CATALOG (pozo-animation-sounds.js).
            // 'entrance' = el cristalino de victoria original, square oscillator — ya probado.
            soundCatalogKey: 'entrance',
        },

        // ── Duración total permitida ──
        // El scheduler usa estos valores para calcular cuántos ciclos de sorteo
        // necesita postponer. El front-tv adapta la duración del warp dentro de este rango.
        minimumAnimationDurationMs: 30000,
        maximumAnimationDurationMs: 90000,

        // ── Timing de cada fase de la animación ──
        // Flujo: espera → dígitos → aceleración → warp → frenado → highlight → revelación → celebración
        animationPhaseTiming: {
            // Espera entre el resultado del sorteo y el inicio de la animación del pozo
            // El público necesita ver el número ganador antes de que la animación lo cubra
            waitTimeAfterDrawResultBeforeAnimationStartMs: 3000,

            // Dígitos del monto del premio girando de 0 al valor final (counter animado)
            prizeAmountCounterAnimationDurationMs: 16500,

            // Delay entre el trigger de la animación y el inicio del counter de dígitos
            // Da tiempo a que las monedas y el título aparezcan antes de que el monto empiece a girar
            prizeAmountCounterDelayAfterTriggerMs: 800,

            // Rodillo: aceleración de velocidad 0 a velocidad warp (ease-in cuadrático)
            reelAccelerationFromZeroToWarpDurationMs: 6000,

            // Rodillo: velocidad constante durante el warp en píxeles por milisegundo
            // A esta velocidad los items son ilegibles (blur visual puro)
            reelConstantWarpSpeedPixelsPerMs: 3.0,

            // Rodillo: duración mínima del warp constante (antes de empezar a frenar)
            // A mayor valor, más dramática la velocidad sostenida
            reelMinimumWarpDurationMs: 8000,

            // Rodillo: frenado de velocidad warp a 0 (ease-out cuadrático)
            reelDecelerationFromWarpToZeroDurationMs: 4000,

            // Rodillo: tiempo que el ganador parpadea destacado antes de la revelación
            // El público identifica quién ganó mientras el highlight flashea
            reelWinnerHighlightDisplayDurationMs: 3000,

            // Revelación: transición visual del layout — los elementos se reagrupan
            // El título se transforma, la cifra se reposiciona, la tarjeta de ganador aparece
            winnerRevealLayoutTransformDurationMs: 1200,

            // Celebración: tiempo que la tarjeta del ganador permanece visible
            // Suficiente para que alguien que no estaba mirando la vea al alzar la vista
            winnerCelebrationCardDisplayDurationMs: 12000,
        },

        // ── Rodillo: configuración del reel ──
        reelConfiguration: {
            // Items legibles en la presentación (los que el público reconoce)
            maximumLegiblePresentationItems: 15,

            // Filas visibles en el viewport del rodillo.
            // 2.5 = fila completa arriba del winner + winner centrado + media fila abajo.
            // Da sensación de "lista que sigue" vs el winner aislado (antes 1.7 cortaba
            // demasiado a ras del item central).
            viewportVisibleRows: 2.5,

            // Padding vertical entre items del rodillo (px)
            rowVerticalPaddingPx: 4,
        },

        // ── Textos de celebración por idioma ──
        // El front-tv resuelve el idioma activo y pasa estos textos a la animación.
        // El título principal NO va aquí — viene del pozo (display_title || nombre).
        celebrationTextsPerLanguage: {
            es: {
                winnerRevealTitle: '¡GANADOR!',
                pointOfSaleLabel: 'Ganado en:',
                nextJackpotActiveLabel: 'Nuevo pozo activo',
                independentDrawCallToAction: 'Cada sorteo es independiente — puede salir en cualquier momento!',
                participantTypeTicketLabel: 'Ticket',
                participantTypePhoneLabel: 'Usuario',
                participantTypeWalletLabel: 'Wallet',
            },
            en: {
                winnerRevealTitle: 'WINNER!',
                pointOfSaleLabel: 'Won at:',
                nextJackpotActiveLabel: 'New jackpot active',
                independentDrawCallToAction: 'Every draw is independent — it can hit at any moment!',
                participantTypeTicketLabel: 'Ticket',
                participantTypePhoneLabel: 'User',
                participantTypeWalletLabel: 'Wallet',
            },
            pt: {
                winnerRevealTitle: 'GANHADOR!',
                pointOfSaleLabel: 'Ganho em:',
                nextJackpotActiveLabel: 'Novo poço ativo',
                independentDrawCallToAction: 'Cada sorteio é independente — pode sair a qualquer momento!',
                participantTypeTicketLabel: 'Bilhete',
                participantTypePhoneLabel: 'Usuário',
                participantTypeWalletLabel: 'Carteira',
            },
            ru: {
                winnerRevealTitle: 'ПОБЕДИТЕЛЬ!',
                pointOfSaleLabel: 'Выиграно в:',
                nextJackpotActiveLabel: 'Новый джекпот активен',
                independentDrawCallToAction: 'Каждый тираж независим — может выпасть в любой момент!',
                participantTypeTicketLabel: 'Билет',
                participantTypePhoneLabel: 'Пользователь',
                participantTypeWalletLabel: 'Кошелек',
            },
        },

        // ── Presets de rendimiento por tier de hardware ──
        // El front-tv elige el tier según las capacidades del dispositivo.
        // 'low': Android WebView legacy, smart TVs baratas
        // 'high': Smart TVs modernas, Chrome en PC
        performancePresetsByHardwareTier: {
            low: {
                maximumCoinParticles: 150,
                maximumStarParticles: 10,
                useProgressiveCoinLoading: true,
                progressiveInitialCoinBatch: 50,
                progressiveCoinBatchSize: 30,
                progressiveCoinLoadIntervalMs: 1000,
                minimumAcceptableFramesPerSecond: 30,
            },
            high: {
                maximumCoinParticles: 3000,
                maximumStarParticles: 20,
                useProgressiveCoinLoading: true,
                progressiveInitialCoinBatch: 50,
                progressiveCoinBatchSize: 50,
                progressiveCoinLoadIntervalMs: 1000,
                minimumAcceptableFramesPerSecond: 35,
            },
        },
    },

    // ── Futuros temas se añaden aquí ──
    // 'confetti-fiesta': { ... },
    // 'neon-cyber': { ... },
};

// ════════════════════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Obtiene un tema por su identificador. FAIL-FAST si no existe.
 * @param {string} themeIdentifier — ej: 'vegas-gold'
 * @returns {Object} definición completa del tema
 */
function getAnimationThemeByIdentifier(themeIdentifier) {
    const theme = POZO_ANIMATION_THEMES[themeIdentifier];
    if (!theme) {
        console.error('[PozoAnimationThemes] Tema no encontrado:', themeIdentifier, '— usando vegas-gold');
        return POZO_ANIMATION_THEMES['vegas-gold'];
    }
    return theme;
}

/**
 * Lista de temas disponibles para selectores del admin.
 * @returns {Array<{id: string, name: string, description: string}>}
 */
function getAvailableAnimationThemesForSelector() {
    const themes = [];
    const keys = Object.keys(POZO_ANIMATION_THEMES);
    for (let i = 0; i < keys.length; i++) {
        const theme = POZO_ANIMATION_THEMES[keys[i]];
        themes.push({
            id: theme.themeIdentifier,
            name: theme.themeDisplayName,
            description: theme.themeDescription,
        });
    }
    return themes;
}

/**
 * Rutas de video de fondo de todos los temas, para prefetch al arrancar front-tv.
 * El manager emite <link rel="prefetch"> por cada una en init().
 * @returns {Array<string>} paths únicos (algunos temas pueden compartir fondo en el futuro)
 */
function listAllBackgroundVideoPathsForPrefetch() {
    const paths = [];
    const seen = {};
    const keys = Object.keys(POZO_ANIMATION_THEMES);
    for (let i = 0; i < keys.length; i++) {
        const path = POZO_ANIMATION_THEMES[keys[i]].backgroundVideoPath;
        if (path && !seen[path]) {
            paths.push(path);
            seen[path] = true;
        }
    }
    return paths;
}

/**
 * Resuelve los textos de celebración para un idioma específico.
 * Fallback a español si el idioma no está disponible.
 * @param {string} themeIdentifier
 * @param {string} languageCode — 'es', 'en', 'pt', 'ru'
 * @returns {Object} textos resueltos
 */
function resolveCelebrationTextsForLanguage(themeIdentifier, languageCode) {
    const theme = getAnimationThemeByIdentifier(themeIdentifier);
    const texts = theme.celebrationTextsPerLanguage;
    return texts[languageCode] || texts['es'];
}

/**
 * Calcula la duración total de las fases fijas de la animación (sin contar el warp variable).
 * El warp se adapta al tiempo disponible entre la duración fija y el máximo del tema.
 * Los dígitos del premio giran en paralelo con el reel — no se suman.
 * Útil para que el scheduler sepa el mínimo de tiempo a reservar para el reschedule.
 * @param {string} themeIdentifier
 * @returns {number} milisegundos
 */
function calculateFixedPhasesTotalDurationMs(themeIdentifier) {
    const timing = getAnimationThemeByIdentifier(themeIdentifier).animationPhaseTiming;
    return (
        timing.waitTimeAfterDrawResultBeforeAnimationStartMs +
        timing.reelAccelerationFromZeroToWarpDurationMs +
        timing.reelMinimumWarpDurationMs +
        timing.reelDecelerationFromWarpToZeroDurationMs +
        timing.reelWinnerHighlightDisplayDurationMs +
        timing.winnerRevealLayoutTransformDurationMs +
        timing.winnerCelebrationCardDisplayDurationMs
    );
}

// ════════════════════════════════════════════════════════════════════════════
// EXPORTS — Dual ESM/CJS
// ════════════════════════════════════════════════════════════════════════════

if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        POZO_ANIMATION_THEMES,
        getAnimationThemeByIdentifier,
        getAvailableAnimationThemesForSelector,
        resolveCelebrationTextsForLanguage,
        calculateFixedPhasesTotalDurationMs,
        listAllBackgroundVideoPathsForPrefetch,
    };
}

export {
    POZO_ANIMATION_THEMES,
    getAnimationThemeByIdentifier,
    getAvailableAnimationThemesForSelector,
    resolveCelebrationTextsForLanguage,
    calculateFixedPhasesTotalDurationMs,
    listAllBackgroundVideoPathsForPrefetch,
};
